'use client';

import React, { useCallback, useEffect, useState } from 'react';

import { Info } from 'lucide-react';

import { Button } from '@/components/ui/button';

import Modal from '@/shared/components/Modal';
import { Tooltip } from '@/shared/components/tooltip';
import { COPY } from '@/shared/constants/copies';
import { ExtraCreditsTier, ISExtraCredits } from '@/shared/services/payment/payment.types';

import { ExtraCreditsPicker } from './extra-credits-picker';

type ExtraCreditsModalProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	extraCredits: ISExtraCredits[];
	currentTier: ExtraCreditsTier | null;
	/**
	 * Seeds the selection when it differs from what the workspace owns — the
	 * pre-checkout upsell uses it to preselect the tier carried over from the
	 * current plan without marking that row as already owned. Defaults to
	 * `currentTier`.
	 */
	initialTier?: ExtraCreditsTier | null;
	onConfirm: (tier: ExtraCreditsTier | null) => void;
	isLoading?: boolean;
	isConfirming?: boolean;
	/** Lets the user pick the plan's included limit. */
	allowNone?: boolean;
	/** The plan's own monthly credits, so options can show totals. */
	planCredits?: number;
	/** Pre-checkout plan price; enables the checkout-specific footer. */
	planPrice?: number;
	currency?: string;
	title?: string;
	description?: string;
};

/**
 * The one extra-credits chooser. Billing and the pre-checkout upsell both render this so a
 * change to the tier list or its wording can never apply to only one of them.
 *
 * Owns the pending selection but not the catalog or the mutation — the caller decides
 * what confirming means (start checkout, or change the live subscription).
 */
export const ExtraCreditsModal = React.memo(function ExtraCreditsModal({
	open,
	onOpenChange,
	extraCredits,
	currentTier,
	initialTier,
	onConfirm,
	isLoading,
	isConfirming,
	allowNone,
	planCredits,
	planPrice,
	currency,
	title,
	description,
}: ExtraCreditsModalProps) {
	const defaultTier = initialTier !== undefined ? initialTier : currentTier;
	const [selectedTier, setSelectedTier] = useState<ExtraCreditsTier | null>(defaultTier);

	// Re-sync whenever the modal reopens.
	useEffect(() => {
		if (open) setSelectedTier(defaultTier);
	}, [defaultTier, open]);

	// Nothing to submit when the selection is what the workspace already has.
	const isUnchanged = selectedTier === currentTier;
	const isPreCheckout = planPrice !== undefined;
	const totalPrice =
		planPrice !== undefined && selectedTier !== null ? planPrice + selectedTier : null;

	const handleConfirm = useCallback(() => onConfirm(selectedTier), [onConfirm, selectedTier]);

	return (
		<Modal
			open={open}
			onOpenChange={onOpenChange}
			titleContent={title ?? COPY.billing.upsellTitle}
			descriptionContent={description ?? COPY.billing.creditsExplainer}
			contentClassName="max-w-md"
			footerContent={
				<div className="flex w-full items-center justify-between gap-3">
					{totalPrice !== null ? (
						<div className="flex items-center gap-1.5">
							<span className="text-sm font-medium text-primary">
								{COPY.billing.upsellTotalPrice(totalPrice, currency)}
							</span>
							<Tooltip
								text={COPY.billing.upsellPriceBreakdown(
									planPrice ?? 0,
									selectedTier ?? 0,
									totalPrice,
									currency
								)}
							>
								<button
									type="button"
									aria-label={COPY.billing.upsellPriceBreakdownLabel}
									className="text-secondary transition-colors hover:text-primary"
								>
									<Info className="size-3.5 shrink-0" />
								</button>
							</Tooltip>
						</div>
					) : null}
					<div className="ml-auto flex items-center gap-2">
						{!isPreCheckout ? (
							<Button
								variant="ghost"
								onClick={() => onOpenChange(false)}
								disabled={isConfirming}
							>
								{COPY.billing.extraCreditsCancel}
							</Button>
						) : null}
						<Button
							onClick={handleConfirm}
							disabled={isLoading || (!isPreCheckout && isUnchanged)}
							disabledWithSpinner={isConfirming}
						>
							{COPY.billing.upsellContinue}
						</Button>
					</div>
				</div>
			}
		>
			<ExtraCreditsPicker
				extraCredits={extraCredits}
				selectedTier={selectedTier}
				onSelect={setSelectedTier}
				currentTier={currentTier}
				allowNone={allowNone}
				planCredits={planCredits}
				currency={currency}
				isLoading={isLoading}
				disabled={isConfirming}
			/>
		</Modal>
	);
});
